import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import type { EventTag, EventTagCategory, ContentFlag, ContentFlagStatus, ResolveContentFlagInput, ChurchOutreachEvent, ChurchApprovalStatus } from '@fellowship/shared'
import type {
  AdminUser,
  AdminChurch,
  AdminChurchDetail,
  AdminEvent,
  AuditLogEntry,
  AdminAutoScrapeResult,
  AdminScrapeResult,
  AdminDeepScrapeResult,
  AdminWaitlistEntry,
  AdminChurchRequest,
  ChurchDuplicatePair,
  AutoResolveDuplicatesResult,
  JobDefinition,
  JobRun,
  OutreachStats,
  OutreachSyncResult,
} from '../types/admin'

// ─── Users ────────────────────────────────────────────────────────────────────

export function useAdminUsers(params: { q?: string; page: number; showDeleted?: boolean }) {
  const { q, page, showDeleted } = params
  const search = new URLSearchParams({ page: String(page), limit: '20' })
  if (q) search.set('q', q)
  if (showDeleted) search.set('showDeleted', 'true')
  return useQuery({
    queryKey: ['admin', 'users', page, q, showDeleted],
    queryFn: () =>
      apiFetch<{ data: AdminUser[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/users?${search}`,
      ),
  })
}

export function useAdminAllUsers(params: { showDeleted?: boolean }) {
  const { showDeleted } = params
  const search = new URLSearchParams({ limit: '1000' })
  if (showDeleted) search.set('showDeleted', 'true')
  return useQuery({
    queryKey: ['admin', 'users', 'all', showDeleted],
    queryFn: () =>
      apiFetch<{ data: AdminUser[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/users?${search}`,
      ),
  })
}

export function useAdminCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string; displayName: string; role: string }) =>
      apiFetch<{ data: AdminUser }>('/admin/users', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useAdminUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      role?: string
      displayName?: string
      email?: string
      password?: string
    }) => apiFetch(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useAdminDeleteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useAdminRestoreUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/users/${id}/restore`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

// ─── Churches ─────────────────────────────────────────────────────────────────

export function useAdminChurches(params: {
  page: number
  showDeleted?: boolean
  q?: string
  denomination?: string
  sort?: string
  sortDir?: 'asc' | 'desc'
}) {
  const { page, showDeleted, q, denomination, sort, sortDir } = params
  const search = new URLSearchParams({ page: String(page), limit: '20' })
  if (showDeleted) search.set('showDeleted', 'true')
  if (q) search.set('q', q)
  if (denomination) search.set('denomination', denomination)
  if (sort) search.set('sort', sort)
  if (sortDir) search.set('sortDir', sortDir)
  return useQuery({
    queryKey: ['admin', 'churches', page, showDeleted, q, denomination, sort, sortDir],
    queryFn: () =>
      apiFetch<{ data: AdminChurch[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/churches?${search}`,
      ),
  })
}

export function useAdminCreateChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ data: AdminChurchDetail }>('/admin/churches', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] }),
  })
}

export function useAdminUpdateChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiFetch(`/admin/churches/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', id] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', 'scraped'] })
    },
  })
}

export function useAdminToggleVerified() {
  const update = useAdminUpdateChurch()
  return {
    mutate: (args: { id: string; verified: boolean }) =>
      update.mutate({ id: args.id, verified: args.verified }),
    isPending: update.isPending,
  }
}

export function useAdminDeleteChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/churches/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] }),
  })
}

export function useAdminRestoreChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/churches/${id}/restore`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] }),
  })
}

export function useAdminChurchDetail(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'churches', id],
    queryFn: () => apiFetch<{ data: AdminChurchDetail }>(`/admin/churches/${id}`),
    enabled: Boolean(id),
  })
}

export function useAdminAddChurchMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, userId, role }: { churchId: string; userId: string; role: string }) =>
      apiFetch(`/admin/churches/${churchId}/admins`, {
        method: 'POST',
        body: JSON.stringify({ userId, role }),
      }),
    onSuccess: (_d, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId] })
    },
  })
}

export function useAdminRemoveChurchMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, userId }: { churchId: string; userId: string }) =>
      apiFetch(`/admin/churches/${churchId}/admins/${userId}`, { method: 'DELETE' }),
    onSuccess: (_d, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId] })
    },
  })
}

export function useAdminUpdateChurchMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, userId, role }: { churchId: string; userId: string; role: string }) =>
      apiFetch(`/admin/churches/${churchId}/admins/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: (_data, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId] })
    },
  })
}

// ─── Events ───────────────────────────────────────────────────────────────────

export function useAdminEvents(params: {
  page: number
  showDeleted?: boolean
  q?: string
  eventType?: string
  status?: string
  source?: string
  sort?: string
  sortDir?: 'asc' | 'desc'
}) {
  const { page, showDeleted, q, eventType, status, source, sort, sortDir } = params
  const search = new URLSearchParams({ page: String(page), limit: '20' })
  if (showDeleted) search.set('showDeleted', 'true')
  if (q) search.set('q', q)
  if (eventType) search.set('eventType', eventType)
  if (status) search.set('status', status)
  if (source) search.set('source', source)
  if (sort) search.set('sort', sort)
  if (sortDir) search.set('sortDir', sortDir)
  return useQuery({
    queryKey: ['admin', 'events', page, showDeleted, q, eventType, status, source, sort, sortDir],
    queryFn: () =>
      apiFetch<{ data: AdminEvent[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/events?${search}`,
      ),
  })
}

export function useAdminEvent(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'events', 'detail', id],
    queryFn: () => apiFetch<{ data: AdminEvent }>(`/admin/events/${id}`),
    enabled: Boolean(id),
  })
}

export function useAdminCreateEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ data: AdminEvent }>('/admin/events', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export interface ImportEventItem {
  title: string
  eventType: string
  description?: string
  addressText?: string
  startsAt: string
  endsAt?: string
}

export function useAdminImportEvents() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, events }: { churchId: string; events: ImportEventItem[] }) =>
      apiFetch<{ data: { imported: number; total: number; errors: string[] } }>(
        '/admin/events/import',
        {
          method: 'POST',
          body: JSON.stringify({ churchId, events }),
        },
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export interface ExtractedEventItem {
  title: string
  description: string | null
  eventType: string
  startsAt: string
  endsAt: string | null
}

export function useAdminExtractEvents() {
  return useMutation({
    mutationFn: (payload: {
      churchId: string
      images?: Array<{ data: string; mediaType: string }>
      text?: string
    }) =>
      apiFetch<{ data: { events: ExtractedEventItem[] } }>('/admin/events/extract', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  })
}

export function useAdminUpdateEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiFetch(`/admin/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export function useAdminDeleteEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/events/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export function useAdminRestoreEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/events/${id}/restore`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

// ─── Event tags (filter attributes) ──────────────────────────────────────────

export function useAdminEventTags() {
  return useQuery({
    queryKey: ['admin', 'event-tags'],
    queryFn: () => apiFetch<{ data: EventTag[] }>('/admin/event-tags'),
    select: (res) => res.data,
  })
}

function invalidateEventTags(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['admin', 'event-tags'] })
  // Public list drives the event forms + Discover filter — refresh it too.
  void queryClient.invalidateQueries({ queryKey: ['event-tags'] })
}

export function useAdminCreateEventTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { label: string; category: EventTagCategory; slug?: string }) =>
      apiFetch<{ data: EventTag }>('/admin/event-tags', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateEventTags(queryClient),
  })
}

export function useAdminUpdateEventTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      label?: string
      category?: EventTagCategory
      sortOrder?: number
      isActive?: boolean
    }) => apiFetch(`/admin/event-tags/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => invalidateEventTags(queryClient),
  })
}

export function useAdminDeleteEventTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/event-tags/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateEventTags(queryClient),
  })
}

// ─── Outreach ─────────────────────────────────────────────────────────────────

export function useAdminChurchOutreach() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (churchId: string) =>
      apiFetch(`/admin/churches/${churchId}/outreach`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] }),
  })
}

// ─── Outreach: Mailchimp funnel tracking ───────────────────────────────────────

export function useAdminSyncChurchesToMailchimp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (churchIds: string[]) =>
      apiFetch<{ data: OutreachSyncResult }>('/admin/churches/outreach/sync', {
        method: 'POST',
        body: JSON.stringify({ churchIds }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'outreach', 'stats'] })
    },
  })
}

export function useAdminBulkUpdateChurchOutreach() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchIds, stage }: { churchIds: string[]; stage: 'exported' | 'opened' | 'clicked' | 'responded' | 'unsubscribed' }) =>
      apiFetch<{ data: OutreachSyncResult }>('/admin/churches/outreach/bulk-update', {
        method: 'POST',
        body: JSON.stringify({ churchIds, stage }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'outreach', 'stats'] })
    },
  })
}

export function useAdminChurchOutreachRespond() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, notes }: { churchId: string; notes?: string }) =>
      apiFetch(`/admin/churches/${churchId}/outreach/respond`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }),
    onSuccess: (_d, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId, 'outreach', 'events'] })
    },
  })
}

export function useAdminUpdateChurchOutreach() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      churchId,
      nextFollowUpAt,
      notes,
    }: {
      churchId: string
      nextFollowUpAt?: string | null
      notes?: string | null
    }) =>
      apiFetch(`/admin/churches/${churchId}/outreach`, {
        method: 'PATCH',
        body: JSON.stringify({ nextFollowUpAt, notes }),
      }),
    onSuccess: (_d, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId, 'outreach', 'events'] })
    },
  })
}

export function useAdminChurchOutreachEvents(churchId: string | null) {
  return useQuery({
    queryKey: ['admin', 'churches', churchId, 'outreach', 'events'],
    queryFn: () => apiFetch<{ data: ChurchOutreachEvent[] }>(`/admin/churches/${churchId}/outreach/events`),
    enabled: Boolean(churchId),
  })
}

export function useAdminOutreachStats() {
  return useQuery({
    queryKey: ['admin', 'outreach', 'stats'],
    queryFn: () => apiFetch<{ data: OutreachStats }>('/admin/outreach/stats'),
  })
}

// ─── Duplicate churches (issue #81) ────────────────────────────────────────────

export function useAdminChurchDuplicates() {
  return useQuery({
    queryKey: ['admin', 'churches', 'duplicates'],
    queryFn: () => apiFetch<{ data: ChurchDuplicatePair[] }>('/admin/churches/duplicates'),
    staleTime: 60 * 1000,
  })
}

export function useAdminDismissDuplicatePair() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchIdA, churchIdB }: { churchIdA: string; churchIdB: string }) =>
      apiFetch('/admin/churches/duplicates/dismiss', {
        method: 'POST',
        body: JSON.stringify({ churchIdA, churchIdB }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', 'duplicates'] }),
  })
}

export function useAdminAutoResolveDuplicates() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ data: AutoResolveDuplicatesResult }>('/admin/churches/duplicates/auto-resolve', {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', 'duplicates'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
    },
  })
}

// ─── Claim requests ───────────────────────────────────────────────────────────

export type AdminClaimRequestStatus = 'pending' | 'approved' | 'denied'

export interface AdminClaimRequest {
  id: string
  churchId: string
  userId: string
  status: AdminClaimRequestStatus
  autoApproved: boolean
  createdAt: string
  reviewedAt: string | null
  churchName?: string
  churchEmail?: string | null
  churchWebsiteUrl?: string | null
  userEmail?: string
  userDisplayName?: string
  reviewedByEmail?: string | null
  reviewedByDisplayName?: string | null
}

export function useAdminClaimRequests(status: AdminClaimRequestStatus = 'pending') {
  const search = new URLSearchParams({ status, limit: '100' })
  return useQuery({
    queryKey: ['admin', 'claim-requests', status],
    queryFn: () => apiFetch<{ data: AdminClaimRequest[] }>(`/admin/claim-requests?${search}`),
  })
}

export function useAdminApproveClaimRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (requestId: string) =>
      apiFetch(`/admin/claim-requests/${requestId}/approve`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'claim-requests'] }),
  })
}

export function useAdminDenyClaimRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (requestId: string) =>
      apiFetch(`/admin/claim-requests/${requestId}/deny`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'claim-requests'] }),
  })
}

// ─── Church approvals (self-serve creation review, issue #179) ─────────────────

export interface AdminPendingChurch {
  id: string
  name: string
  slug: string
  city: string | null
  state: string | null
  denomination: string
  websiteUrl: string | null
  email: string | null
  createdAt: string
  ownerUserId: string | null
  ownerEmail: string | null
  ownerDisplayName: string | null
  approvalStatus: ChurchApprovalStatus
  reviewedAt: string | null
  reviewedByEmail: string | null
  reviewedByDisplayName: string | null
  approvalNote: string | null
}

export function useAdminChurchApprovals(status: ChurchApprovalStatus = 'pending') {
  const search = new URLSearchParams({ status, limit: '100' })
  return useQuery({
    queryKey: ['admin', 'church-approvals', status],
    queryFn: () => apiFetch<{ data: AdminPendingChurch[] }>(`/admin/church-approvals?${search}`),
  })
}

export function useAdminApproveChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (churchId: string) =>
      apiFetch(`/admin/church-approvals/${churchId}/approve`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'church-approvals'] }),
  })
}

export function useAdminRejectChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, reason }: { churchId: string; reason?: string }) =>
      apiFetch(`/admin/church-approvals/${churchId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason ?? null }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'church-approvals'] }),
  })
}

// ─── Review queue (scraped items) ─────────────────────────────────────────────

export function useAdminScrapedChurches() {
  return useQuery({
    queryKey: ['admin', 'churches', 'scraped'],
    queryFn: () =>
      apiFetch<{ data: AdminChurch[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/churches?page=1&limit=100`,
      ),
    select: (res) => ({
      ...res,
      data: res.data.filter((c) => !c.claimed && !c.verified),
    }),
  })
}

export function useAdminScrapedEvents() {
  return useQuery({
    queryKey: ['admin', 'events', 'scraped'],
    queryFn: () =>
      apiFetch<{ data: AdminEvent[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/events?page=1&limit=100`,
      ),
    select: (res) => ({
      ...res,
      data: res.data.filter((e) => e.source === 'scraped'),
    }),
  })
}

// ─── Coverage map ─────────────────────────────────────────────────────────────

export interface CoverageChurch {
  id: string
  name: string
  lat: number
  lng: number
  status: 'claimed' | 'active' | 'stale' | 'discovered' | 'bare'
}

export function useAdminCoverage() {
  return useQuery({
    queryKey: ['admin', 'coverage'],
    queryFn: () => apiFetch<{ data: CoverageChurch[] }>('/admin/coverage'),
    staleTime: 60_000,
  })
}

export interface DiscoverCandidate {
  osmId: number
  osmType: string
  name: string
  denomination: string | null
  address: string | null
  lat: number
  lng: number
  phone: string | null
  email: string | null
  websiteUrl: string | null
  alreadyExists: boolean
}

export interface ImportCandidate {
  name: string
  denomination: string | null
  address: string | null
  lat: number
  lng: number
  phone: string | null
  email: string | null
  websiteUrl: string | null
}

export function useAdminDiscoverPreview() {
  return useMutation({
    mutationFn: (body: { lat: number; lng: number; radiusKm: number; denominations?: string[] }) =>
      apiFetch<{ data: DiscoverCandidate[] }>('/admin/discover/preview', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  })
}

export interface EnrichResult {
  name: string
  denomination: string | null
  address: string | null
  city: string | null
  state: string | null
  phone: string | null
  email: string | null
}

type EnrichInput = Pick<
  ImportCandidate,
  'name' | 'denomination' | 'address' | 'lat' | 'lng' | 'phone' | 'email' | 'websiteUrl'
> & {
  city?: string | null
  state?: string | null
}

export function useAdminDiscoverEnrich() {
  return useMutation({
    mutationFn: (candidates: EnrichInput[]) =>
      apiFetch<{ data: EnrichResult[] }>('/admin/discover/enrich', {
        method: 'POST',
        body: JSON.stringify({ candidates }),
      }),
  })
}

export interface EnrichBatchStatus {
  batchId: string
  total: number
  completed: number
  failed: number
  done: boolean
  results: Array<{ osmId: number } & EnrichResult>
}

type EnrichBatchInput = { osmId: number } & EnrichInput

const ENRICH_CHUNK_SIZE = 50

export function useStartEnrichBatch() {
  return useMutation({
    mutationFn: async (candidates: EnrichBatchInput[]) => {
      // Step 1: create the batch record
      const { data } = await apiFetch<{ data: { batchId: string } }>(
        '/admin/discover/enrich-batch',
        {
          method: 'POST',
          body: JSON.stringify({ total: candidates.length }),
        },
      )

      // Step 2: send candidates in chunks of 100
      for (let i = 0; i < candidates.length; i += ENRICH_CHUNK_SIZE) {
        const chunk = candidates.slice(i, i + ENRICH_CHUNK_SIZE)
        await apiFetch(`/admin/discover/enrich-batch/${data.batchId}/items`, {
          method: 'POST',
          body: JSON.stringify({ candidates: chunk }),
        })
      }

      return { data }
    },
  })
}

export function useEnrichBatchStatus(batchId: string | null) {
  return useQuery({
    queryKey: ['admin', 'enrich-batch', batchId],
    queryFn: () =>
      apiFetch<{ data: EnrichBatchStatus }>(`/admin/discover/enrich-batch/${batchId!}`),
    enabled: !!batchId,
    refetchInterval: (query) => {
      const data = query.state.data?.data
      return data?.done ? false : 3000
    },
    staleTime: 0,
  })
}

export function useAdminEnrichChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (churchId: string) =>
      apiFetch<{ data: EnrichResult & { updated: string[] } }>(
        `/admin/churches/${churchId}/enrich`,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
    },
  })
}

const IMPORT_BATCH_SIZE = 75

export function useAdminDiscoverImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (candidates: ImportCandidate[]) => {
      let totalImported = 0
      const allFailed: { name: string; reason: string }[] = []
      for (let i = 0; i < candidates.length; i += IMPORT_BATCH_SIZE) {
        const batch = candidates.slice(i, i + IMPORT_BATCH_SIZE)
        const res = await apiFetch<{ data: { imported: number; total: number; failed: { name: string; reason: string }[] } }>(
          '/admin/discover/import',
          {
            method: 'POST',
            body: JSON.stringify({ candidates: batch }),
          },
        )
        totalImported += res.data.imported
        if (res.data.failed?.length) allFailed.push(...res.data.failed)
      }
      return { data: { imported: totalImported, total: candidates.length, failed: allFailed } }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'coverage'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
    },
  })
}

export function useAdminDiscoverCheck() {
  return useMutation({
    mutationFn: (candidates: Pick<ImportCandidate, 'name' | 'lat' | 'lng'>[]) =>
      apiFetch<{ data: { name: string; lat: number; lng: number; alreadyExists: boolean }[] }>(
        '/admin/discover/check',
        {
          method: 'POST',
          body: JSON.stringify({ candidates }),
        },
      ),
  })
}

// ─── Scrape ───────────────────────────────────────────────────────────────────

export function useAdminAutoScrape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { churchId: string }) =>
      apiFetch<{ data: AdminAutoScrapeResult }>('/admin/scrape/auto', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] })
    },
  })
}

export function useAdminScrape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { sourceUrl: string; churchId?: string }) =>
      apiFetch<{ data: AdminScrapeResult }>('/admin/scrape', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] })
    },
  })
}

// Manual-trigger multi-hop crawl (issue #250) — follows bulletin/events/calendar-shaped
// links (and PDFs) up to `maxHops` deep. Not part of the automated weekly scrape.
export function useAdminDeepScrape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { churchId: string; maxHops: number; sourceUrl?: string }) =>
      apiFetch<{ data: AdminDeepScrapeResult }>('/admin/scrape/deep', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] })
    },
  })
}

// Async multi-church deep scrape: queue a hand-picked set of churches (e.g. everyone
// with a website inside a launch-market radius) for the same multi-hop crawl as
// useAdminDeepScrape, one pg-boss job per church instead of one request per church.
export interface DeepScrapeBatchResult {
  churchId: string
  status: 'done' | 'failed'
  eventsCreated?: number
  eventsSkipped?: number
  hopsUsed?: number
  error?: string
}

export interface DeepScrapeBatchStatus {
  batchId: string
  total: number
  completed: number
  failed: number
  done: boolean
  results: DeepScrapeBatchResult[]
}

export function useStartDeepScrapeBatch() {
  return useMutation({
    mutationFn: (body: { churchIds: string[]; maxHops: number }) =>
      apiFetch<{ data: { batchId: string } }>('/admin/scrape/deep-batch', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  })
}

export function useDeepScrapeBatchStatus(batchId: string | null) {
  return useQuery({
    queryKey: ['admin', 'scrape-deep-batch', batchId],
    queryFn: () =>
      apiFetch<{ data: DeepScrapeBatchStatus }>(`/admin/scrape/deep-batch/${batchId!}`),
    enabled: !!batchId,
    refetchInterval: (query) => (query.state.data?.data.done ? false : 3000),
    staleTime: 0,
  })
}

// ─── Waitlist ─────────────────────────────────────────────────────────────────

export function useAdminWaitlist(page: number) {
  const search = new URLSearchParams({ page: String(page), limit: '30' })
  return useQuery({
    queryKey: ['admin', 'waitlist', page],
    queryFn: () =>
      apiFetch<{
        data: AdminWaitlistEntry[]
        meta: { total: number; page: number; limit: number }
      }>(`/admin/waitlist?${search}`),
  })
}

export function useAdminAllWaitlist() {
  return useQuery({
    queryKey: ['admin', 'waitlist', 'all'],
    queryFn: () =>
      apiFetch<{
        data: AdminWaitlistEntry[]
        meta: { total: number; page: number; limit: number }
      }>('/admin/waitlist?limit=1000'),
  })
}

// ─── Church requests ──────────────────────────────────────────────────────────

export function useAdminChurchRequests(page: number) {
  const search = new URLSearchParams({ page: String(page), limit: '30' })
  return useQuery({
    queryKey: ['admin', 'church-requests', page],
    queryFn: () =>
      apiFetch<{
        data: AdminChurchRequest[]
        meta: { total: number; page: number; limit: number }
      }>(`/admin/church-requests?${search}`),
  })
}

export function useAdminAllChurchRequests() {
  return useQuery({
    queryKey: ['admin', 'church-requests', 'all'],
    queryFn: () =>
      apiFetch<{
        data: AdminChurchRequest[]
        meta: { total: number; page: number; limit: number }
      }>('/admin/church-requests?limit=1000'),
  })
}

// ─── Audit log ────────────────────────────────────────────────────────────────

export function useAdminAuditLog(params: { targetType?: string; page: number }) {
  const { targetType, page } = params
  const search = new URLSearchParams({ page: String(page), limit: '30' })
  if (targetType) search.set('targetType', targetType)
  return useQuery({
    queryKey: ['admin', 'audit-log', page, targetType],
    queryFn: () =>
      apiFetch<{ data: AuditLogEntry[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/audit-log?${search}`,
      ),
  })
}

// ─── All churches (server-side search, DataTable client-side sort/filter) ─────

export function useAdminAllChurches(
  params: { showDeleted?: boolean; q?: string; location?: string; denomination?: string; hasEmail?: 'yes' | 'no' } = {},
) {
  const search = new URLSearchParams({ page: '1', limit: '1000' })
  if (params.showDeleted) search.set('showDeleted', 'true')
  if (params.q) search.set('q', params.q)
  if (params.location) search.set('location', params.location)
  if (params.denomination) search.set('denomination', params.denomination)
  if (params.hasEmail) search.set('hasEmail', params.hasEmail)
  return useQuery({
    queryKey: ['admin', 'churches', 'all', params.showDeleted, params.q ?? '', params.location ?? '', params.denomination ?? '', params.hasEmail ?? ''],
    queryFn: () =>
      apiFetch<{ data: AdminChurch[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/churches?${search}`,
      ),
    staleTime: 60 * 1000,
  })
}

// ─── All events (high-limit, for DataTable client-side filtering) ─────────────

export function useAdminAllEvents(params: { showDeleted?: boolean } = {}) {
  const search = new URLSearchParams({ page: '1', limit: '200' })
  if (params.showDeleted) search.set('showDeleted', 'true')
  return useQuery({
    queryKey: ['admin', 'events', 'all', params.showDeleted],
    queryFn: () =>
      apiFetch<{ data: AdminEvent[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/events?${search}`,
      ),
    staleTime: 60 * 1000,
  })
}

// ─── Jobs ─────────────────────────────────────────────────────────────────────

export function useAdminJobs() {
  return useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: () => apiFetch<{ data: JobDefinition[] }>('/admin/jobs'),
    staleTime: Infinity,
  })
}

export function useAdminJobRuns(jobName?: string) {
  return useQuery({
    queryKey: ['admin', 'job-runs', jobName],
    queryFn: () => {
      const search = new URLSearchParams({ limit: '200' })
      if (jobName) search.set('jobName', jobName)
      return apiFetch<{ data: JobRun[] }>(`/admin/job-runs?${search}`)
    },
    refetchInterval: 30_000,
  })
}

// ─── Content moderation ───────────────────────────────────────────────────────

export function useAdminModeration(params: { page: number; status?: ContentFlagStatus }) {
  const { page, status = 'pending' } = params
  const search = new URLSearchParams({ page: String(page), limit: '50', status })
  return useQuery({
    queryKey: ['admin', 'moderation', page, status],
    queryFn: () =>
      apiFetch<{ data: ContentFlag[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/moderation?${search}`,
      ),
  })
}

export function useAdminResolveFlag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: string } & ResolveContentFlagInput) =>
      apiFetch(`/admin/moderation/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation'] }),
  })
}
